"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

interface Props {
  /** CSS selector for the article container that holds the MDX headings. */
  contentSelector?: string;
}

/**
 * Sticky sidebar Table of Contents.
 * Scans the rendered article DOM for h2/h3 with ids, then highlights the
 * heading currently in view via IntersectionObserver.
 */
export default function BlogTableOfContents({
  contentSelector = "#blog-article-content",
}: Props) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const root = document.querySelector(contentSelector);
    if (!root) return;

    const nodes = Array.from(
      root.querySelectorAll<HTMLHeadingElement>("h2[id], h3[id]"),
    );

    const parsed: Heading[] = nodes
      .filter((n) => n.id && n.textContent)
      .map((n) => ({
        id: n.id,
        text: n.textContent ?? "",
        level: (n.tagName === "H3" ? 3 : 2) as 2 | 3,
      }));

    // Derived from rendered MDX DOM — must read after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHeadings(parsed);

    if (parsed.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 },
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [contentSelector]);

  if (headings.length < 2) return null;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const lenis = typeof window !== "undefined" ? window.__lenis : null;
    const offset = 96;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    if (lenis) {
      lenis.scrollTo(top, { duration: 1 });
    } else {
      window.scrollTo({ top, behavior: "smooth" });
    }
    history.replaceState(null, "", `#${id}`);
  };

  return (
    <nav aria-label="Table of contents" className="text-sm">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">
        On this page
      </p>
      <ul className="space-y-2 border-l border-border-subtle">
        {headings.map((h) => (
          <li key={h.id} className={cn(h.level === 3 && "ml-3")}>
            <a
              href={`#${h.id}`}
              onClick={(e) => handleClick(e, h.id)}
              className={cn(
                "block -ml-px border-l-2 border-transparent py-1 pl-3 text-text-muted transition-colors hover:text-text-primary",
                activeId === h.id &&
                  "border-brand-purple-light text-text-primary",
              )}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
